import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import UseTemplateBtnIcon from 'components/atoms/icons/useTemplateBtnIcon';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useEffect, type FC } from 'react';
import DraggableModal from 'components/organisms/draggableModal';

import { clinicalNotesValidationSchema } from './validation-schema';
import s from './styles.module.scss';

import type { FormikHelpers } from 'formik';
import type { Values } from './validation-schema';
import type { IAddClinicNotes } from 'infrastructure/interfaces';

interface ClinicalNotesFormProps {
  title: string;
  open: boolean;
  onCancelForm: () => void;
  onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => void;
  loading: boolean;
  onUseTemplate?: () => void;
  onUseRpmSpecialistTemplate?: () => void;
  onCcmCkdTemplate?: () => void;
  defaultValues?: Omit<IAddClinicNotes, 'patientGuid'>;
}

const ClinicalNotesModalForm: FC<ClinicalNotesFormProps> = ({
  title,
  open,
  loading,
  onCancelForm,
  onSubmit,
  onUseTemplate,
  onUseRpmSpecialistTemplate,
  onCcmCkdTemplate,
  defaultValues,
}) => {
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const formik = useFormik<Values>({
    initialValues: clinicalNotesValidationSchema().cast(defaultValues),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: clinicalNotesValidationSchema(),
  });

  const { submitForm, resetForm, values } = formik;

  const onCancel = async () => {
    if (!loading) {
      if (values.note) {
        const confirmRes = await showAlert({
          title: t('confirm.cancelNote'),
          messageTitle: t('confirm.closeClinicalNotePopup'),
          successBtnLabel: t('controls.yes'),
          rejectBtnLabel: t('controls.no'),
        });

        if (confirmRes) {
          resetForm();
          onCancelForm();
        }
      } else {
        resetForm();
        onCancelForm();
      }
    }
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  return (
    <>
      {open && (
        <DraggableModal
          title={title}
          isOpen={open}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <BaseForm
            formik={formik}
            withControls
            plaintext={false}
            readonly={false}
          >
            <BaseFormItem name="note">
              <BaseTextArea rows={10} placeholder="Type your note here" />
            </BaseFormItem>
            <div className={s.footer}>
              <div className={s.left}>
                {onUseTemplate && (
                  <BaseButton
                    className={s['use-template-button']}
                    label={t('controls.useTemplate')}
                    dataCy="useTemplate-button"
                    type="secondary"
                    disabled={loading}
                    icon={<UseTemplateBtnIcon />}
                    onClick={onUseTemplate}
                  />
                )}
                {onUseRpmSpecialistTemplate && (
                  <BaseButton
                    className={s['use-template-button']}
                    label={t('controls.generateDeviceTroubleshooting')}
                    dataCy="useTemplate-button"
                    type="secondary"
                    disabled={loading}
                    icon={<UseTemplateBtnIcon />}
                    onClick={onUseRpmSpecialistTemplate}
                  />
                )}
                {onCcmCkdTemplate && (
                  <BaseButton
                    className={s['use-template-button']}
                    label={t('controls.ckdTemplate')}
                    dataCy="useCKDTemplate-button"
                    type="secondary"
                    disabled={loading}
                    icon={<UseTemplateBtnIcon />}
                    onClick={onCcmCkdTemplate}
                  />
                )}
              </div>

              <div className={s.right}>
                <BaseButton
                  label={t('controls.cancel')}
                  dataCy="clinicalNotes-cancel-button"
                  type="secondary"
                  onClick={onCancel}
                  disabled={loading}
                />
                <BaseButton
                  label={t('controls.save')}
                  dataCy="clinicalNotes-save-button"
                  onClick={submitForm}
                  disabled={loading}
                  loading={loading}
                />
              </div>
            </div>
          </BaseForm>
        </DraggableModal>
      )}
      {Alert}
    </>
  );
};
export default ClinicalNotesModalForm;
